"use strict";

/**
 * Clase `Selection` para gestionar funcionalidades donde se debe escoger.
 */
export class Selection {
    /**
     * Crea una instancia de la clase Selection.
     * @param {Object} notification - Objeto para manejar notificaciones.
     * @param {Object} httpError - Objeto para manejar errores HTTP.
     * @param {Object} tool - Objeto para métodos auxiliares
     */
    constructor(notification, httpError, tool) {
        this.notification = notification;
        this.httpError = httpError;
        this.tool = tool;
    }

    /**
     * Gestiona la selección de títulos académicos desde el formulario de estudios
     */
    chooseAcademicTitle() {
        const selectors = [
            "#academic_title_id",
            "#div_external_academic_title",
            "#external_academic_title",
            "#label_external_academic_title",
        ];

        if (!this.tool.areElementsPresent(selectors)) return;

        const academicTitle = document.querySelector("#academic_title_id");
        const divExternalAcademicTitle = document.querySelector(
            "#div_external_academic_title"
        );
        const externalAcademicTitle = document.querySelector(
            "#external_academic_title"
        );
        const labelExternalAcademicTitle = document.querySelector(
            "#label_external_academic_title"
        );

        this.tool.initSelectr(academicTitle);

        academicTitle.addEventListener("change", (event) => {
            const selectedText = event.target.selectedOptions[0].text;
            labelExternalAcademicTitle.classList.add("invisible");

            if (selectedText === "OTRO") {
                divExternalAcademicTitle.classList.add("md:grid");
                divExternalAcademicTitle.classList.remove("hidden");
                externalAcademicTitle.required = true;
            } else {
                divExternalAcademicTitle.classList.add("hidden");
                divExternalAcademicTitle.classList.remove("md:grid");
                externalAcademicTitle.required = false;
                externalAcademicTitle.value = "";
            }
        });
    }

    /**
     * Gestiona la selección de solicitudes para aprobación
     */
    chooseApplications() {
        if (!this.tool.areElementsPresent(["#table_applications"])) return;

        /** Obtiene los elementos relevantes del DOM */
        const actNumber = document.querySelector("#act_number");
        const divAct = document.querySelector("#div_act");
        const divExtension = document.querySelector("#div_extension");
        const extension0 = document.querySelector("#is_extension_0");
        const extension1 = document.querySelector("#is_extension_1");
        const formDateApproved = document.querySelector("#form_date_approved");
        const inputApplications = document.querySelector("#applications");
        const inputEmpirical = document.querySelector("#empirical");
        const inputExtension = document.querySelector("#extension");

        let selected = new Set(); // Conjunto de solicitudes seleccionadas
        let empiricalSelected = 0; // Contador de trámites empíricos seleccionados
        let permissionSelected = 0; // Contador de permisos de trabajo seleccionados

        document.querySelectorAll(".on_aprobation").forEach((item) => {
            item.addEventListener("click", () => {
                const consecutive = item.id;
                const procedure = parseInt(item.getAttribute("alt"), 10);

                /** Cambia los estilos visuales del elemento al ser seleccionado/deseleccionado */
                item.classList.toggle("mark_to_documentation");
                item.classList.toggle("text-gray-500");
                item.classList.toggle("text-green-500");
                item.classList.toggle("hover:bg-gray-500");
                item.classList.toggle("hover:bg-green-500");

                /** Actualiza los conjuntos de selección dependiendo de la acción */
                if (item.classList.contains("mark_to_documentation")) {
                    selected.add(consecutive);

                    if (procedure === 8) permissionSelected++;
                    if (procedure === 10) empiricalSelected++;
                } else {
                    selected.delete(consecutive);

                    if (procedure === 8)
                        permissionSelected = Math.max(
                            0,
                            permissionSelected - 1
                        );

                    if (procedure === 10)
                        empiricalSelected = Math.max(0, empiricalSelected - 1);
                }

                const countSelected = selected.size;

                /** Valida máximo 3 solicitudes seleccionadas */
                if (countSelected > 3) {
                    this.notification.showToasted(
                        "alive",
                        "error",
                        "<p class='m-0 text-center'>Por favor elija como máximo 3 solicitudes antes de continuar</p>"
                    );
                }

                /** Muestra/oculta el formulario de aprobación dependiendo del número de seleccionados */
                formDateApproved.classList.toggle(
                    "hidden",
                    countSelected <= 0 || countSelected > 3
                );
                formDateApproved.classList.toggle(
                    "inline-flex",
                    countSelected > 0 && countSelected <= 3
                );

                /** Muestra/oculta el campo de número de acta si hay trámites empíricos */
                divAct.classList.toggle("hidden", empiricalSelected <= 0);
                divAct.classList.toggle("md:grid", empiricalSelected > 0);
                actNumber.required = empiricalSelected > 0;

                if (empiricalSelected <= 0) actNumber.value = "";

                /** Muestra/oculta el campo de extensión si hay permisos de trabajo */
                divExtension.classList.toggle(
                    "hidden",
                    permissionSelected <= 0
                );
                divExtension.classList.toggle(
                    "md:grid",
                    permissionSelected > 0
                );
                extension0.required = permissionSelected > 0;
                extension1.required = permissionSelected > 0;

                if (permissionSelected <= 0) {
                    extension0.checked = false;
                    extension1.checked = false;
                }

                /** Actualiza los valores ocultos para el backend */
                inputApplications.value = [...selected].join(",");
                inputEmpirical.value = empiricalSelected > 0 ? 1 : 0;
                inputExtension.value = permissionSelected > 0 ? 1 : 0;
            });
        });
    }

    /**
     * Selecciona una universidad/instituto desde el formulario de estudios
     */
    chooseInstitution() {
        const selectors = [
            "#academic_title_id",
            "#div_academic_title_id",
            "#div_external_academic_title",
            "#div_external_institution",
            "#external_academic_title",
            "#external_institution",
            "#institution_id",
            "#label_external_academic_title",
        ];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos DOM relevantes */
        const academicTitle = document.querySelector("#academic_title_id");
        const divAcademicTitle = document.querySelector(
            "#div_academic_title_id"
        );
        const divExternalAcademicTitle = document.querySelector(
            "#div_external_academic_title"
        );
        const divExternalInstitution = document.querySelector(
            "#div_external_institution"
        );
        const externalAcademicTitle = document.querySelector(
            "#external_academic_title"
        );
        const externalInstitution = document.querySelector(
            "#external_institution"
        );
        const institution = document.querySelector("#institution_id");
        const labelExternalAcademicTitle = document.querySelector(
            "#label_external_academic_title"
        );

        institution.addEventListener("change", (event) => {
            const selectedText = event.target.selectedOptions[0].text;

            if (selectedText === "OTRO") {
                /** Si se selecciona 'OTRO', muestra los campos para nueva institución y título académico */
                divAcademicTitle.classList.add("hidden");
                divAcademicTitle.classList.remove("md:grid");

                divExternalInstitution.classList.add("md:grid");
                divExternalInstitution.classList.remove("hidden");

                divExternalAcademicTitle.classList.add("md:grid");
                divExternalAcademicTitle.classList.remove("hidden");

                labelExternalAcademicTitle.classList.remove("invisible");

                academicTitle.required = false;
                externalInstitution.required = true;
                externalAcademicTitle.required = true;
            } else {
                /** Si se selecciona una institución existente */
                divAcademicTitle.classList.add("md:grid");
                divAcademicTitle.classList.remove("hidden");
                academicTitle.required = true;

                /** Si no hay valor escrito en el campo de institución externa lo oculta */
                if (!externalInstitution.value) {
                    divExternalInstitution.classList.add("hidden");
                    divExternalInstitution.classList.remove("md:grid");
                    externalInstitution.required = false;
                    externalInstitution.value = "";
                }

                /** Si no se seleccionó 'OTRO' como título académico y no hay título externo se oculta */
                if (
                    academicTitle.value !==
                        "99cf5da9-a4c6-4842-8a2f-435a13c3f04c" &&
                    externalAcademicTitle.value === ""
                ) {
                    divExternalAcademicTitle.classList.add("hidden");
                    divExternalAcademicTitle.classList.remove("md:grid");
                    externalAcademicTitle.required = false;
                    externalAcademicTitle.value = "";
                }

                labelExternalAcademicTitle.classList.add("invisible");
            }
        });
    }

    /**
     * Retorna la lista de requerimientos de un trámite
     */
    chooseProcedure() {
        const selectors = ["#div_fields", "#div_instructions", "#procedure_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos DOM relevantes */
        const divFields = document.querySelector("#div_fields");
        const divInstructions = document.querySelector("#div_instructions");
        const procedure = document.querySelector("#procedure_id");
        const spinner = document.querySelector("#spinner");

        this.tool.initSelectr(procedure);

        procedure.addEventListener("change", (event) => {
            /** Muestra el spinner de carga mientras se realiza la petición */
            spinner?.classList.remove("hidden");

            const procedureValue = procedure.value;

            this.tool
                .makeRequest(
                    "put",
                    `/fields/get/requirements/${procedureValue}`,
                    { procedureValue }
                )
                .then((response) => {
                    /** Oculta el spinner una vez recibida la respuesta */
                    spinner?.classList.add("hidden");

                    /** Muestra el contenedor de instrucciones y campos */
                    divInstructions.classList.remove("hidden");
                    divFields.classList.remove("hidden");

                    /** Actualiza el contenido de los campos requeridos según la respuesta del servidor */
                    divFields.innerHTML = response.data;
                })
                .catch((error) => {
                    this.httpError.handleHttpError(error);
                });
        });
    }

    /**
     * Selecciona un rol desde el formulario de creación de usuarios
     */
    chooseRoleOnCreate() {
        const selectors = ["#div_fields_applicant", "#role_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos del DOM relevantes */
        const role = document.querySelector("#role_id");
        const divFieldsApplicant = document.querySelector(
            "#div_fields_applicant"
        );
        const address = document.querySelector("#address");
        const cellPhone = document.querySelector("#cell_phone");
        const dateBirth = document.querySelector("#date_of_birth");
        const dateExpedition = document.querySelector("#date_of_expedition");
        const gender = document.querySelector("#gender_id");
        const landline = document.querySelector("#landline");
        const region = document.querySelector("#region_id");
        const regionExpedition = document.querySelector(
            "#region_expedition_id"
        );

        this.tool.initSelectr(role);

        role.addEventListener("change", (event) => {
            const selectedText = event.target.selectedOptions[0].text;

            if (selectedText === "Solicitante") {
                /** Si se elige "Solicitante", muestra los campos adicionales y los hace obligatorios */
                divFieldsApplicant.classList.add("md:grid");
                divFieldsApplicant.classList.remove("hidden");

                cellPhone.required = true;
                dateBirth.required = true;
                gender.required = true;
                region.required = true;
                regionExpedition.required = true;
            } else {
                /** Si se elige otro rol, oculta los campos adicionales y quita la obligatoriedad */
                divFieldsApplicant.classList.add("hidden");
                divFieldsApplicant.classList.remove("md:grid");

                cellPhone.required = false;
                dateBirth.required = false;
                gender.required = false;
                region.required = false;
                regionExpedition.required = false;

                /** Limpia los valores de los campos adicionales */
                address.value = "";
                cellPhone.value = "";
                dateBirth.value = "";
                dateExpedition.value = "";
                landline.value = "";
            }
        });
    }

    /**
     * Selecciona un rol desde el formulario de edición de usuarios
     */
    chooseRoleOnEdit() {
        const selectors = ["#div_user_special", "#role_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos relevantes del DOM */
        const role = document.querySelector("#role_id");
        const divUserSpecial = document.querySelector("#div_user_special");
        const profession = document.querySelector("#profession");
        const professionalRegistration = document.querySelector(
            "#professional_registration"
        );
        const signature = document.querySelector("#signature");

        const specialRoles = ["6", "7"];

        /** Si el rol actual es Presidente (6) o Secretario (7) al cargar la página */
        if (specialRoles.includes(role.value)) {
            profession.required = true;
            professionalRegistration.required = true;
        } else {
            profession.required = false;
            professionalRegistration.required = false;
            signature.required = false;
        }

        this.tool.initSelectr(role);

        role.addEventListener("change", (event) => {
            const selectedText = event.target.selectedOptions[0].text;

            /** Sí se elige 'Presidente' y/o 'Secretario' */
            if (selectedText == "Presidente" || selectedText == "Secretario") {
                /** Si se elige Presidente o Secretario, muestra los campos especiales y los hace obligatorios */
                divUserSpecial.classList.add("md:grid");
                divUserSpecial.classList.remove("hidden");

                profession.required = true;
                professionalRegistration.required = true;
                signature.required = true;
            } else {
                /** Si se elige otro rol, oculta los campos especiales y quita la obligatoriedad */
                divUserSpecial.classList.add("hidden");
                divUserSpecial.classList.remove("md:grid");

                profession.required = false;
                professionalRegistration.required = false;
                signature.required = false;

                /** Limpia los valores de los campos especiales */
                profession.value = "";
                professionalRegistration.value = "";
                signature.value = "";
            }
        });
    }

    /**
     * Selecciona una etapa desde el formulario de gestión de solicitudes
     */
    chooseStage() {
        const selectors = ["#div_resolution", "#resolution", "#stage_id"];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos del DOM relevantes */
        const divResolution = document.querySelector("#div_resolution");
        const resolution = document.querySelector("#resolution");
        const stage = document.querySelector("#stage_id");

        stage.addEventListener("change", (event) => {
            const selectedValue = event.target.selectedOptions[0].value;

            if (selectedValue === "5") {
                /** Si se selecciona "Pendiente de pago" muestra el campo de resolución */
                divResolution.classList.add("md:grid");
                divResolution.classList.remove("hidden");
                resolution.required = true;
            } else {
                /** Si se selecciona otra etapa, oculta el campo de resolución y limpia el valor */
                divResolution.classList.add("hidden");
                divResolution.classList.remove("md:grid");
                resolution.required = false;
                resolution.value = "";
            }
        });
    }

    /**
     * Selecciona una etapa en el formulario de anotaciones.
     * Carga dinámicamente las categorías correspondientes a la etapa seleccionada,
     * y muestra u oculta el campo de fecha de finalización según corresponda.
     */
    chooseStageCard() {
        const selectors = [
            "#stage_card_id",
            "#div_category",
            "#div_end_date_at",
        ];

        if (!this.tool.areElementsPresent(selectors)) return;

        /** Elementos del DOM relevantes */
        const divCategory = document.querySelector("#div_category");
        const divEndDate = document.querySelector("#div_end_date_at");
        const endDateAt = document.querySelector("#end_date_at");
        const stageCard = document.querySelector("#stage_card_id");

        this.tool.initSelectr(stageCard);

        /** Función para manejar el cambio de etapa */
        const handleStageCardChange = () => {
            const value = parseInt(stageCard.value, 10);

            if (!value) {
                divCategory.innerHTML = "";
                return;
            }

            /** Muestra/oculta el campo de fecha de finalización dependiendo del valor */
            if (value === 3) {
                divEndDate.classList.add("md:grid");
                divEndDate.classList.remove("hidden");
                endDateAt.required = true;
            } else {
                divEndDate.classList.add("hidden");
                divEndDate.classList.remove("md:grid");
                endDateAt.required = false;
            }

            /** Carga las categorías correspondientes */
            this.tool
                .makeRequest("put", `/admin/categories/${value}`, {
                    stageCardValue: value,
                })
                .then((response) => {
                    divCategory.classList.add("md:grid");
                    divCategory.classList.remove("hidden");
                    divCategory.innerHTML = response.data;

                    const category = document.querySelector("#category_id");
                    this.tool.initSelectr(category);
                })
                .catch((error) => {
                    this.httpError.handleHttpError(error);
                });
        };

        /** Ejecuta al cargar la página si hay valor inicial */
        handleStageCardChange();

        /** Ejecuta cuando se cambie la etapa manualmente */
        stageCard.addEventListener("change", handleStageCardChange);
    }
}
